import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Building2, Clock, FileCheck, HeartHandshake, Home, Landmark, Palette, ShieldCheck, Sparkles, Users } from "lucide-react";
import { Kicker, Reveal, useTitle } from "../components/Reveal";
import { SERVICES } from "../data/content";

const ICONS = { Sparkles, Clock, Home, Palette, FileCheck, Users, Building2, ShieldCheck, Landmark, HeartHandshake };

export const AudiencePage = ({ data, pageId }) => {
  useTitle(`${data.overline} – Manfred Berglar · Malermeister seit 1925`);
  const services = SERVICES.filter((s) => data.serviceIds.includes(s.id));

  return (
    <div className="bg-white text-ink overflow-x-clip">
      {/* Hero */}
      <section className="relative bg-ink overflow-hidden" data-testid={`${pageId}-hero`}>
        <div className="absolute inset-0">
          <img src={data.image} alt={`${data.overline} – Manfred Berglar, Malermeister`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/75 via-ink/40 to-ink/5" />
        </div>
        <div className="container-x relative z-10 pt-[150px] md:pt-[190px] pb-16 md:pb-24">
          <Reveal>
            <p className="kicker-dark" data-testid={`${pageId}-overline`}>{data.overline}</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 data-testid={`${pageId}-title`} className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02] text-white mt-4 max-w-3xl">
              {data.title}
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-white/75 leading-relaxed mt-6 max-w-2xl">{data.sub}</p>
          </Reveal>
          <Reveal delay={0.24}>
            <Link to="/kontakt" data-testid={`${pageId}-hero-cta`} className="btn-light mt-9">
              Projekt anfragen <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Value Points */}
      <section className="py-24 md:py-32" data-testid={`${pageId}-points`}>
        <div className="container-x">
          <Reveal>
            <Kicker>Warum Berglar</Kicker>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5 mt-8">
            {data.points.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <Reveal key={p.title} delay={i * 0.06}>
                  <div className="card-arch p-8 h-full" data-testid={`${pageId}-point-${i}`}>
                    <Icon size={24} className="text-golddark" strokeWidth={1.5} />
                    <h3 className="font-display text-xl font-semibold tracking-tight text-ink mt-5">{p.title}</h3>
                    <p className="text-muted leading-relaxed mt-2.5 text-sm">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Relevant Services */}
      <section className="bg-surface border-y border-line py-24 md:py-32" data-testid={`${pageId}-services`}>
        <div className="container-x">
          <Reveal>
            <Kicker>Passende Leistungen</Kicker>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-ink mt-4">
              Gefragt bei {data.overline}
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {services.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.07} className="h-full">
                <Link to="/leistungen" data-testid={`${pageId}-service-${s.id}`} className="card-arch group block h-full overflow-hidden">
                  <div className="overflow-hidden aspect-[16/10]">
                    <img
                      src={s.image}
                      alt={`${s.title} – Manfred Berglar, Malermeister`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 border-t-2 border-gold">
                    <h3 className="font-display text-lg font-semibold tracking-tight text-ink">{s.title}</h3>
                    <p className="text-muted text-sm leading-relaxed mt-2">{s.tags.join(" · ")}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-24 md:py-32" data-testid={`${pageId}-cta`}>
        <div className="container-x">
          <Reveal>
            <div className="relative bg-navy bg-grain text-white p-10 md:p-14 lg:p-16 overflow-hidden rounded-2xl">
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-herald" aria-hidden="true" />
              <img
                src="/images/wappen.png"
                alt=""
                aria-hidden="true"
                className="absolute -right-8 -bottom-12 h-[280px] w-auto opacity-[0.07] pointer-events-none select-none hidden md:block"
              />
              <div className="grid lg:grid-cols-12 gap-8 items-end relative">
                <div className="lg:col-span-8">
                  <p className="kicker-dark">{data.overline}</p>
                  <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1] mt-4 max-w-xl">
                    {data.ctaTitle}
                  </h2>
                  <p className="text-white/65 leading-relaxed mt-4 max-w-lg">{data.ctaText}</p>
                </div>
                <div className="lg:col-span-4">
                  <Link to="/kontakt" data-testid={`${pageId}-cta-button`} className="btn-light">
                    Jetzt anfragen <ArrowUpRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};
